import React, { useCallback } from 'react';

import { ConvertMarkdownModeDialogBase } from '@atlassian/editor-plugin-markdown-mode/confirmation-dialog';

import { useConvertDialogActions, useConvertDialogState } from './ConvertDialogStore.ts';

export type ConvertMarkdownModeDialogContainerProps = {
	/**
	 * Confirm handler for `direction === 'toMarkdown'`. Should run the BE
	 * flip + (optional) FE rewrite. Resolves to `{ success }` so the dialog
	 * can render its inline error state on failure.
	 */
	onConfirmToMarkdown: () => Promise<{ success: boolean }>;
	/**
	 * Confirm handler for `direction === 'toRichText'`. Should run the BE
	 * flip. Resolves to `{ success }`.
	 */
	onConfirmToRichText: () => Promise<{ success: boolean }>;
};

/**
 * Thin Confluence-side wrapper around the platform
 * `ConvertMarkdownModeDialogBase`. Holds no UI of its own — its only job
 * is to bind the sweet-state store (`ConvertDialogStore`) to the
 * presentational dialog and route the confirm handler by direction.
 *
 * Rendered exactly once per page (in `MoreActions`). All three entry
 * points (overflow menu, title `.md` gesture, `cmd+shift+x`) open this
 * dialog by dispatching `openConvertDialog` through `useMarkdownModeActions`.
 */
export const ConvertMarkdownModeDialogContainer = ({
	onConfirmToMarkdown,
	onConfirmToRichText,
}: ConvertMarkdownModeDialogContainerProps): React.JSX.Element => {
	const [{ dialog }] = useConvertDialogState();
	const { closeConvertDialog } = useConvertDialogActions();

	const handleConfirm = useCallback(async () => {
		if (!dialog.isOpen) {
			return { success: false };
		}
		return dialog.direction === 'toMarkdown' ? onConfirmToMarkdown() : onConfirmToRichText();
	}, [dialog, onConfirmToMarkdown, onConfirmToRichText]);

	return (
		<ConvertMarkdownModeDialogBase
			isOpen={dialog.isOpen}
			direction={dialog.isOpen ? dialog.direction : 'toMarkdown'}
			isLossy={dialog.isOpen ? dialog.isLossy : false}
			syncBlockCount={dialog.isOpen ? dialog.syncBlockCount : 0}
			onConfirm={handleConfirm}
			onClose={closeConvertDialog}
		/>
	);
};
